<template>
    <SectionListLayout
        :section_state="folders_controller.section_state"
        state_id="fetch_all"
        content_class="content list-group"
        loading_list="folders"
        title="folders.title"
    >
       <template v-if='folders_selection_collection.count === 0' #actions>
            <ActionButton
                @click="onReloadFolders()"
                v-tooltip='"folders.actions.refresh"'
                :icon="folders_controller.section_state.loading['fetch_background'] === true ? 'fas fa-redo-alt fa-spin' : 'fas fa-redo-alt'"

            />
            <ActionButton v-if="folders_controller.mailboxes.folders.custom && folders_controller.mailboxes.folders.custom.count > 0"
                @click="onFoldersSearchToggle()"
                v-tooltip='"folders.actions.search_folders"'
                icon='fas fa-search'
                :class="{'active' : folders_search_visible}"
            />
            <ActionButton
                @click="onFoldersAdd()"
                v-tooltip='"folders.actions.add_folder"'
                icon='fas fa-plus-circle'
                :gtm="{
                  module : 'folders',
                  feature: 'folders_create_modal',
                  block  : 'top_actions',
                  action : 'open'
                }"
            />
        </template>
         <template v-else #actions_over>
            <ActionButton
                @click="onFoldersEdit()"
                v-tooltip='"folders.actions.edit"'
                icon='fas fa-edit'
                :gtm="{
                  module : 'folders',
                  feature: 'folders_edit_modal',
                  block  : 'top_actions',
                  action : 'open'
                }"
            />
            <ActionButton
                @click="onFoldersMove()"
                v-tooltip='"folders.actions.move"'
                icon='fas fa-folder-open'
                :gtm="{
                  module : 'folders',
                  feature: 'folders_move_modal',
                  block  : 'top_actions',
                  action : 'open'
                }"
            />
            <ActionButton
                @click="onSubFoldersAdd()"
                v-tooltip='"folders.actions.add_subfolder"'
                icon='fas fa-folder-plus'
                :gtm="{
                  module : 'folders',
                  feature: 'folders_add_subfolder_modal',
                  block  : 'top_actions',
                  action : 'open'
                }"
            />

            <DropdownList class="more-actions align-right">
                <template #select>
                    <ActionButton
                        v-tooltip='"messages.actions.more"'
                        icon='fas fa-ellipsis-v'
                    />
                </template>

                <template #options>
                    <ActionButton
                        @click="onFoldersEmpty()"
                        text='folders.actions.empty'
                        icon='far fa-folder empty-folder'
                        :gtm="{
                          module : 'folders',
                          feature: 'folders_empty_modal',
                          block  : 'top_actions',
                          action : 'open'
                        }"
                    />

                    <ActionButton
                        @click="onFoldersDelete()"
                        text='folders.actions.delete'
                        icon='fas fa-trash-alt'
                        :gtm="{
                          module : 'folders',
                          feature: 'folders_delete_modal',
                          block  : 'top_actions',
                          action : 'open'
                        }"
                    />

                    <ActionButton
                        @click="onFoldersAddFilter()"
                        text='folders.actions.add_filter'
                        icon='fas fa-filter'
                        :gtm="{
                          module : 'folders',
                          feature: 'filters_form',
                          block  : 'top_actions',
                          action : 'open'
                        }"
                    />
                </template>
            </DropdownList>
        </template>

        <template #pre_content>
            <InputSearch v-if="folders_search_visible"
                ref="input_search_component"
                class="folders-search"
                @inputSearchSet='onInputSearchSet'
                @inputSearchClear='onInputSearchClear'
            />
        </template>

        <template #content>
            <FoldersListCollapsable v-if="folders_search_collection.search_active === false && folders_collapse_collection.count > 0"
              ref="folders_collapse_element"
              selection_box="radio"
              list_type="router"
              drag_hover_active="active"
              drag_drop_suffix="folder-list"
              :drag_start_event="drag_event_folder_section"
              :drag_drop_events="[drag_event_folder_section, drag_event_sidebar_folder]"
              :folders_collapse_collection='folders_collapse_collection'
              :folders_selection_collection='folders_selection_collection'
              @folderContextMenu="onFolderContextMenu"
            />

            <FoldersListSearch v-else-if="folders_search_collection.count > 0"
              selection_box="radio"
              list_type="router"
              :folders_search_collection='folders_search_collection'
              :folders_selection_collection='folders_selection_collection'
              @folderContextMenu="onFolderContextMenu"
            />

            <ContentEmpty v-else
              text="folders.empty_folders"
              icon="fas fa-folder-open"
            />

            <ContextMenu ref="context_menu_component">
                <template #menu_items>
                    <FoldersContextActions
                        :webmail_view="webmail_view"
                        :context_folder='context_folder'
                        :state_initiator="initiator"
                        block="context_menu_actions"
                    />
                </template>
            </ContextMenu>
        </template>
    </SectionListLayout>

    <DragDrop
      ref="ref_dragdrop_folders_view"
      event_name="drag-folder-list"
      :icon="{single: 'fas fa-folder'}"
      :message="{single: 'folders.move.drag_move_folder'}"
      :dragdrop_controller="dragdrop_controller"></DragDrop>
</template>

<script setup>
  import {ref, reactive, watch, nextTick}   from 'vue';
  import {onBeforeMount, onUnmounted}       from 'vue';
  import {defineProps}                      from 'vue';
  import {useRouter}                        from 'vue-router';
  import FoldersListCollapsable             from '../Partials/FoldersListCollapsable.vue';
  import FoldersListSearch                  from '../Partials/FoldersListSearch.vue';
  import FoldersContextActions              from '../Partials/FoldersContextActions.vue';
  import SectionListLayout                  from '@/vue/Layouts/Sections/SectionListLayout.vue';
  import InputSearch                        from '@/vue/Elements/Inputs/InputSearch.vue'
  import ContentEmpty                       from '@/vue/Elements/Content/ContentEmpty.vue';
  import ActionButton                       from '@/vue/Elements/Buttons/ActionButton.vue';
  import DropdownList                       from '@/vue/Elements/DropDowns/DropdownList.vue';
  import ContextMenu                        from '@/vue/Elements/Tools/ContextMenu.vue';
  import FoldersController                  from '@/js/Mailboxes/FoldersController';
  import FoldersCollapseCollection          from '@/js/Mailboxes/Collections/FoldersCollapseCollection';
  import FoldersSearchCollection            from '@/js/Mailboxes/Collections/FoldersSearchCollection';
  import StateConsumer                      from '@/js/Shared/State/StateConsumer';
  import DragDropController                 from '@/js/Webmail/DragDrop/DragDropController';
  import DragDrop                           from '@/vue/Elements/Tools/DragDrop.vue';

  const props = defineProps({
    folders_selection_collection : {
      required: true,
    },

    webmail_view : {
      required: true,
    }
  })

  const folders_controller          = FoldersController
  const folders_search_collection   = reactive(new FoldersSearchCollection());
  const folders_collapse_collection = reactive(new FoldersCollapseCollection('folders_list'));
  const context_menu_component      = ref();
  const folders_search_visible      = ref(false);
  const folders_collapse_element    = ref();
  const context_folder              = ref();
  const ref_dragdrop_folders_view   = ref();
  const folders_disabled            = ref({});
  const dragdrop_controller         = DragDropController;
  const router                      = useRouter();
  const initiator                   = 'folders_list';
  const drag_event_folder_section   = 'drag-folder-list';
  const drag_event_sidebar_folder   = 'drag-sidebar-folder-list';

  onBeforeMount(() => {
    folders_controller.broadcaster.on('folders-deleted', onFoldersDeleted);

    folders_controller.section_state.onLoad('fetch_all', async () => {
      setCollectionLists();
    });

    dragdrop_controller.broadcaster.on(`${drag_event_folder_section}-start`, onDragStart);
    dragdrop_controller.broadcaster.on(`${drag_event_folder_section}-end`, onDragEnd);
    // cross events
    dragdrop_controller.broadcaster.on(`${drag_event_folder_section}-enter-folder-list`, onDragEnter);
    dragdrop_controller.broadcaster.on(`${drag_event_folder_section}-leave-folder-list`, onDragLeave);
    dragdrop_controller.broadcaster.on(`${drag_event_folder_section}-drop-folder-list`, onDragDrop);
    dragdrop_controller.broadcaster.on(`${drag_event_sidebar_folder}-enter-folder-list`, onDragEnter);
    dragdrop_controller.broadcaster.on(`${drag_event_sidebar_folder}-leave-folder-list`, onDragLeave);
    dragdrop_controller.broadcaster.on(`${drag_event_sidebar_folder}-drop-folder-list`, onDragDrop);
  })

  onUnmounted(() => {
    folders_controller.broadcaster.destroy('folders-delete', onFoldersDeleted);

    dragdrop_controller.broadcaster.destroy(`${drag_event_folder_section}-start`, onDragStart);
    dragdrop_controller.broadcaster.destroy(`${drag_event_folder_section}-end`, onDragEnd);
    // cross events
    dragdrop_controller.broadcaster.destroy(`${drag_event_folder_section}-enter-folder-list`, onDragEnter);
    dragdrop_controller.broadcaster.destroy(`${drag_event_folder_section}-leave-folder-list`, onDragLeave);
    dragdrop_controller.broadcaster.destroy(`${drag_event_folder_section}-drop-folder-list`, onDragDrop);
    dragdrop_controller.broadcaster.destroy(`${drag_event_sidebar_folder}-enter-folder-list`, onDragEnter);
    dragdrop_controller.broadcaster.destroy(`${drag_event_sidebar_folder}-leave-folder-list`, onDragLeave);
    dragdrop_controller.broadcaster.destroy(`${drag_event_sidebar_folder}-drop-folder-list`, onDragDrop);
  })

  watch(() => folders_controller.collection_state.last_added, async (folder) => {
    setCollectionLists();

    if (folders_controller.collection_state.initiator !== initiator) {
      return;
    }

    if (folders_collapse_collection.isCollapsed(folder.parent) === true) {
      folders_collapse_collection.expand(folder.parent);
    }

    await nextTick();
    folders_collapse_element.value.scrollToItem(folder);
    props.folders_selection_collection.unique(folder);
  });

  watch(folders_controller.collection_state.last_updated, async (folder) => {
    setCollectionLists();

    if (folders_controller.collection_state.initiator !== initiator) {
      return;
    }

    await nextTick();
    folders_collapse_element.value.scrollToItem(folder.current);
    props.folders_selection_collection.unique(folder.current);
  });

  const onFoldersDeleted = (folder) => {
    props.folders_selection_collection.delete(folder.id);
    setCollectionLists();
  }

  const onDragStart = () => {
    dragdrop_controller.setDragOverlayElement(ref_dragdrop_folders_view.value.$el);
    dragdrop_controller.setDragOverlayImage(dragdrop_controller.drags.element_event);
    dragdrop_controller.drags.data.add(dragdrop_controller.drags.initial_data.id, dragdrop_controller.drags.initial_data);

    setFoldersDisabled(folders_controller.getDisabledFolders(dragdrop_controller.drags.initial_data));
  }

  const onDragEnd = () => {
    setFoldersDisabled({});
  }

  const onDragEnter = (drag_item) => {
    folders_collapse_collection.expandDelay(drag_item.drop_data.id);
  }

  const onDragLeave = (drag_item) => {
    folders_collapse_collection.expandDelayStop(drag_item.drop_data.id);
  }

  const onDragDrop = (drag_item) => {
    folders_controller.move(
      drag_item.drop_data,
      drag_item.initial_data
    );
  }

  const setFoldersDisabled = (value) => {
    folders_disabled.value = value;
  }

  const onReloadFolders = () => {
    folders_controller.fetchBackground();
  }

  const onFoldersAdd = () => {
    folders_controller.collection_state.setInitiator(initiator);
    props.webmail_view.openFoldersCreate();
  }

  const onSubFoldersAdd = () => {
    folders_controller.collection_state.setInitiator(initiator);
    props.webmail_view.openFoldersCreate(
      props.folders_selection_collection.getFirst()
    );
  }

  const onFoldersEdit = () => {
    folders_controller.collection_state.setInitiator(initiator);
    props.webmail_view.openFoldersEdit(
      props.folders_selection_collection.getFirst()
    );
  }

  const onFoldersDelete = () => {
    folders_controller.collection_state.setInitiator(initiator);
    props.webmail_view.openFoldersDelete(
      props.folders_selection_collection.getFirst()
    );
  }

  const onFoldersEmpty = () => {
    folders_controller.collection_state.setInitiator(initiator);
    props.webmail_view.openFoldersEmpty(
      props.folders_selection_collection.getFirst()
    );
  }

  const onFoldersMove = () => {
    folders_controller.collection_state.setInitiator(initiator);
    props.webmail_view.openFoldersMove(
      props.folders_selection_collection.getFirst()
    );
  }

  const onFoldersAddFilter = () => {
    StateConsumer.set('filters_folder_select', props.folders_selection_collection.getFirst());
    router.push('/settings/filters/add');
  }

  const onFolderContextMenu = (folder) => {
    props.folders_selection_collection.unique(folder);
    setContextFolder(props.folders_selection_collection.getFirst());
    context_menu_component.value.show();
  }

  const onInputSearchSet = (term) => {
    folders_search_collection.search(term);
  }

  const onInputSearchClear = (term) => {
    folders_search_collection.reset();
  }

  const onFoldersSearchToggle = () => {
    setFoldersSearchVisibility(!folders_search_visible.value);
  }

  const setCollectionLists = () => {
    folders_collapse_collection.setFolders(folders_controller.mailboxes.folders.custom);
    folders_search_collection.setSearchList(folders_controller.mailboxes.folders.custom);

    if (folders_search_collection.count > 0) {
      folders_search_collection.reload();
    }
  }

  const setFoldersSearchVisibility = (value) => {
    folders_search_visible.value = value
  }

  const setContextFolder = (folder) => {
    context_folder.value = folder;
  }
</script>